/**
 * Activity's Service filter (components/collab/ActivityPanel.jsx): which services an event is about, and the events
 * left for some services picked. vfs-service's `GET /collab/activity` already says where each event happened: `files`
 * (typing sessions, agent runs, reverts), `path` (comments, notes, suggestions), `service` (a secret, an email) and
 * `repo` (repo flags). An event about a pinned file's own session (`app--compose-docker-compose-yaml`, an email it sent)
 * is its service's ("app"): vfs-service already answers `service: 'app'` with `serviceLabel` "app · docker-compose.yaml",
 * and the raw name folds the same way here, so the filter never lists the runner's slug. A file under `services/<name>/`
 * belongs to that service; brain/, the root files and the events on the whole project (sharing, data, publishing, pauses, handoffs) are "Project files".
 *
 * A pick is a list of keys: a service's short name ("api") or PROJECT_FILES; empty = all services. Several keys = a
 * group: an event shows when any of its services is picked. The pick is remembered per project in this browser.
 */

import { parseRunnableName } from './runnablesModel';

/** The key of brain/, the root files and the project-wide events (a service name never has a colon). */
export const PROJECT_FILES = ':project';
export const PROJECT_FILES_LABEL = 'Project files';

const STORE_PREFIX = 'flow.activity.services.';

/**
 * "services/api", "api", "/services/api/" → "api"; '' when nothing is named. A pinned file's runner session
 * (`app--compose-docker-compose-yaml`, utils/runnablesModel.js) belongs to the service that holds it: "app".
 */
export function serviceKey(name) {
  const key = String(name || '').trim().replace(/^\/+/, '').replace(/^services\//, '').replace(/\/+$/, '').split('/')[0] || '';
  return parseRunnableName(key)?.service || key;
}

/** The key a project path belongs to: "services/api/src/a.js" → "api", "brain/x.md" or "README.md" → PROJECT_FILES. */
export function keyOfPath(path) {
  const parts = String(path || '').replace(/^\/+/, '').split('/');
  return parts[0] === 'services' && parts.length > 2 && parts[1] ? parts[1] : PROJECT_FILES;
}

const pathOf = (file) => (typeof file === 'string' ? file : file?.path);

/** The keys an event is about, in order, without repeats (never empty: an event about nothing in particular is the project's). */
export function servicesOfEvent(event) {
  const keys = new Set();
  const files = Array.isArray(event?.files) ? event.files.map(pathOf).filter(Boolean) : [];
  files.forEach((file) => keys.add(keyOfPath(file)));
  if (event?.path) { keys.add(keyOfPath(event.path)); }
  if (event?.service) { keys.add(serviceKey(event.service) || PROJECT_FILES); }
  if (event?.repo) { keys.add(String(event.repo).startsWith('services/') ? serviceKey(event.repo) || PROJECT_FILES : PROJECT_FILES); }
  if (keys.size === 0) { keys.add(PROJECT_FILES); }
  return [...keys];
}

/** Whether the event is about any of the picked keys (empty pick: always). */
export function matchesServices(event, picked) {
  if (!picked?.length) { return true; }
  return servicesOfEvent(event).some((key) => picked.includes(key));
}

/** The events about any of the picked keys. */
export const filterByServices = (events, picked) => (events || []).filter((event) => matchesServices(event, picked));

/**
 * The Service filter's rows: `{ key, label, count }`, services by name then Project files last. The rows come from
 * `events` (all of them, so a row does not vanish when another filter hides its events) plus any picked key; the counts
 * from `counted` (the events the other filters leave; defaults to `events`).
 */
export function serviceRows(events, counted = events, picked = []) {
  const counts = new Map();
  (events || []).forEach((event) => servicesOfEvent(event).forEach((key) => { if (!counts.has(key)) { counts.set(key, 0); } }));
  (picked || []).forEach((key) => { if (key && !counts.has(key)) { counts.set(key, 0); } });
  (counted || []).forEach((event) => servicesOfEvent(event).forEach((key) => counts.set(key, (counts.get(key) || 0) + 1)));
  const rows = [...counts.entries()].map(([key, count]) => ({ key, label: key === PROJECT_FILES ? PROJECT_FILES_LABEL : key, count }));
  return rows.sort((a, b) => {
    if (a.key === PROJECT_FILES) { return 1; }
    if (b.key === PROJECT_FILES) { return -1; }
    return a.label.localeCompare(b.label);
  });
}

/** The pick with a key added or taken out. */
export const togglePick = (picked, key) => ((picked || []).includes(key) ? picked.filter((k) => k !== key) : [...(picked || []), key]);

/** The pick from a link's `service` (`?tab=activity&service=api`): one key, or null when it names nothing. */
export function pickFromLink(service) {
  if (service === PROJECT_FILES || service === 'project') { return [PROJECT_FILES]; }
  const key = serviceKey(service);
  return key ? [key] : null;
}

/** Short text for the phone's filter summary: "All services", "api", "api + 2". */
export function pickSummary(picked) {
  if (!picked?.length) { return 'All services'; }
  const first = picked[0] === PROJECT_FILES ? PROJECT_FILES_LABEL : picked[0];
  return picked.length === 1 ? first : `${first} + ${picked.length - 1}`;
}

const clean = (list) => (Array.isArray(list) ? [...new Set(list.filter((k) => typeof k === 'string' && k))] : []);

/** The pick remembered for this project in this browser ([] = all services). */
export function rememberedPick(projectId) {
  if (!projectId) { return []; }
  try {
    return clean(JSON.parse(localStorage.getItem(`${STORE_PREFIX}${projectId}`) || '[]'));
  } catch {
    return [];
  }
}

export function rememberPick(projectId, picked) {
  if (!projectId) { return; }
  try {
    const list = clean(picked);
    if (list.length) { localStorage.setItem(`${STORE_PREFIX}${projectId}`, JSON.stringify(list)); } else { localStorage.removeItem(`${STORE_PREFIX}${projectId}`); }
  } catch { /* not remembered */ }
}
